import p01 from "./posts/01.json";
import p02 from "./posts/02.json";
import p03 from "./posts/03.json";
import p04 from "./posts/04.json";
import p05 from "./posts/05.json";
import p06 from "./posts/06.json";
import p07 from "./posts/07.json";
import p08 from "./posts/08.json";
import p09 from "./posts/09.json";
import p10 from "./posts/10.json";
import p11 from "./posts/11.json";
import p12 from "./posts/12.json";
import p13 from "./posts/13.json";
import p14 from "./posts/14.json";
import p15 from "./posts/15.json";
import p16 from "./posts/16.json";
import p17 from "./posts/17.json";
import p18 from "./posts/18.json";
import p19 from "./posts/19.json";
import p20 from "./posts/20.json";
import p21 from "./posts/21.json";
import p22 from "./posts/22.json";
import p23 from "./posts/23.json";
import p24 from "./posts/24.json";
import p25 from "./posts/25.json";
import p26 from "./posts/26.json";
import p27 from "./posts/27.json";
import p28 from "./posts/28.json";
import p29 from "./posts/29.json";
import p30 from "./posts/30.json";

export interface BlogLink { href: string; label: string }
export interface BlogSection { heading: string; paragraphs: string[]; bullets?: string[] }
export interface BlogPostSource {
  id: number; title: string; category: string; slug: string;
  seoTitle: string; metaDescription: string; primaryKeyword: string; supportingKeywords: string[];
  intro: string; sections: BlogSection[];
  cta: { heading: string; text: string; label: string; href: string };
  serviceLinks: BlogLink[]; sources?: BlogLink[];
}
export interface BlogPost extends BlogPostSource {
  path: string; image: string; imageAlt: string; wordCount: number; readingMinutes: number;
}

export const BLOG_PATH = "/blog";

// Deliberate, one-to-one editorial choices: never fall back to category images.
const IMAGES: Record<number, { file: string; alt: string }> = {
  1: { file: "sauna-assembly.webp", alt: "Sauna cabin assembly in progress with panels and tools prepared on site" },
  2: { file: "sauna-cabin-3040b-sp-br-front.webp", alt: "Glass-front sauna cabin showing the benches, heater and fittings included in a project budget" },
  3: { file: "sauna-cabin-3040c-sp-cl-front.webp", alt: "Wide timber sauna cabin with glazed doors and tiered bench seating" },
  4: { file: "sauna-detail-2.webp", alt: "Timber sauna wall panels and bench construction details" },
  5: { file: "sauna-cabin-3040a-hem-cl-top-shot.webp", alt: "Overhead sauna layout showing bench dimensions, heater position and floor space" },
  6: { file: "sauna-detail-5.webp", alt: "Sauna entrance and interior illustrating the room layout considered when planning ventilation" },
  7: { file: "outdoor-infrared-garden.webp", alt: "Outdoor timber sauna cabin in a snowy garden setting" },
  8: { file: "home-sauna-slide-3.webp", alt: "Glass-front sauna integrated into a bathroom beside a shower and bath" },
  9: { file: "sauna-cabin-3040a-sp-br-front-left-side-1.webp", alt: "Enclosed timber sauna cabin with solid side walls and a glazed entrance" },
  10: { file: "sauna-fitting.webp", alt: "Engineer fitting a sauna entrance during installation" },
  11: { file: "sauna-heater.webp", alt: "Electric sauna heater filled with stones beside timber walls" },
  12: { file: "sauna-design.webp", alt: "Sauna interior with a heater flue, timber benches and a glazed entrance" },
  13: { file: "sauna-maintenance.webp", alt: "Close view of stones stacked inside a sauna heater basket" },
  14: { file: "home-sauna-slide-2.webp", alt: "Sauna bucket and ladle placed on a timber bench beside the heater" },
  15: { file: "sauna-supply-delivery.webp", alt: "Wrapped sauna components secured on pallets inside a delivery vehicle" },
  16: { file: "sauna-detail-3.webp", alt: "Installed sauna temperature controller and electrical control equipment" },
  17: { file: "sauna-detail-4.webp", alt: "Sauna interior showing coloured lighting and illuminated timber benches" },
  18: { file: "home-sauna-slide-1.webp", alt: "Glass-front sauna with accessible timber benches and floor slats for cleaning" },
  19: { file: "sauna-engineer-cover.webp", alt: "Engineer inspecting the interior of a timber sauna" },
  20: { file: "sauna-supply.webp", alt: "Close view of a stone-filled electric sauna heater" },
  21: { file: "sauna-detail-1.webp", alt: "Timber sauna benches and wall cladding beneath warm concealed lighting" },
  22: { file: "sauna-maintenance-cover.webp", alt: "Engineer working beside sauna control equipment" },
  23: { file: "home-sauna-slide-4.webp", alt: "Large illuminated sauna beside a shared wellness and relaxation area" },
  24: { file: "sauna-install.webp", alt: "Timber sauna room with tiered benches and a window" },
  25: { file: "sauna-cabin-3040b-hem-cl-front-left-side.webp", alt: "Spacious glazed sauna cabin with seating arranged along two walls" },
  26: { file: "outdoor-infrared-plan.webp", alt: "Sauna plan showing the entrance, L-shaped seating and infrared panel positions" },
  27: { file: "sauna-cabin-3040b-sp-cl-front.webp", alt: "Front elevation of a glazed sauna with lighting, benches and heater" },
  28: { file: "sauna-panel-assembly.webp", alt: "Sauna bench and timber components being checked and assembled on site" },
  29: { file: "sauna-cabin-3040a-sp-cl-front-left-side.webp", alt: "Timber sauna cabin illustrating the enclosure and internal fittings to compare in supplier quotes" },
  30: { file: "outdoor-infrared-cabin.webp", alt: "Completed outdoor infrared sauna cabin with pitched roof and glazed door" },
};

const words = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;

function enrich(p: BlogPostSource): BlogPost {
  const img = IMAGES[p.id];
  if (!img) throw new Error(`Missing editorial image for blog post ${p.id}`);
  const wordCount = words(p.title) + words(p.intro) + p.sections.reduce((n, s) =>
    n + words(s.heading) + s.paragraphs.reduce((a, x) => a + words(x), 0) + (s.bullets ?? []).reduce((a, x) => a + words(x), 0), 0)
    + words(p.cta.heading) + words(p.cta.text);
  return { ...p, sources: p.sources ?? [], path: `${BLOG_PATH}/${p.slug}`, image: img.file, imageAlt: img.alt, wordCount, readingMinutes: Math.max(1, Math.round(wordCount / 220)) };
}

const raw = [
  p01,
  p02,
  p03,
  p04,
  p05,
  p06,
  p07,
  p08,
  p09,
  p10,
  p11,
  p12,
  p13,
  p14,
  p15,
  p16,
  p17,
  p18,
  p19,
  p20,
  p21,
  p22,
  p23,
  p24,
  p25,
  p26,
  p27,
  p28,
  p29,
  p30,
] as unknown as BlogPostSource[];

export const blogPosts: BlogPost[] = raw.map(enrich).sort((a, b) => a.id - b.id);

export const blogCategories: string[] = Array.from(new Set(blogPosts.map(p => p.category)));

/** Match every query word across the article's public summary and topic keywords. */
export function filterBlogPosts(query: string, category = ""): BlogPost[] {
  const terms = query.trim().toLocaleLowerCase("en-GB").split(/\s+/).filter(Boolean);
  return blogPosts.filter(post => {
    if (category && post.category !== category) return false;
    const searchable = [post.title, post.metaDescription, post.category, post.primaryKeyword, ...post.supportingKeywords]
      .join(" ").toLocaleLowerCase("en-GB");
    return terms.every(term => searchable.includes(term));
  });
}

export const findBlogPost = (slug: string): BlogPost | undefined =>
  blogPosts.find(p => p.slug === slug.replace(/^\/?blog\//, "").replace(/\/+$/, ""));

export function relatedBlogPosts(post: BlogPost, count = 3): BlogPost[] {
  const same = blogPosts.filter(p => p.id !== post.id && p.category === post.category);
  const others = blogPosts.filter(p => p.id !== post.id && p.category !== post.category);
  const near = (a: BlogPost, b: BlogPost) => Math.abs(a.id - post.id) - Math.abs(b.id - post.id);
  return [...same.sort(near), ...others.sort(near)].slice(0, count);
}

export const slugifyHeading = (s: string) =>
  s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
